import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Languages, Stages } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import {
  AllMapLocations,
  MapLocationPayload,
  MapType,
  createLocation,
  deleteLocations,
  getLocation,
  mapLocations,
  mapTypes,
  updateLocation,
} from '../../../core/services/map'
import { getAllStages } from '../../../core/services/stage'
import SelectField from '@/components/select-field'
import { Button, Card, Checkbox, Input, Modal, Textarea } from '@/components/ui'
import { PageTitle } from '@/components'
import { cn } from '@/lib/utils'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

interface IMapTypeContent {
  locale: string
  name: string
  description: string
}

interface IMapLocationValues {
  type: string
  content: IMapTypeContent[]
  stage?: string
  latitude: string
  longitude: string
  published: boolean
}

export interface CreateEditLocationProps {
  visible: boolean
  setVisible: (show: boolean) => void
  item: AllMapLocations | undefined
  onClose?: () => void
  page: number
  size: number
  allLanguages: Languages[]
}

export const CreateEditLocation = (
  props: CreateEditLocationProps,
): JSX.Element => {
  const { visible, setVisible, item, onClose, size, page, allLanguages } = props
  const [stagesList, setStagesList] = useState<Stages[]>([])
  const [mapTypesList, setMapTypesList] = useState<MapType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [
    showDeleteConfirmationModalLocation,
    setShowDeleteConfirmationModalLocation,
  ] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    if (visible) {
      const temp = map(allLanguages, (lang) => {
        return {
          locale: lang.locale,
          description: '',
          name: '',
        }
      })
      setValue('content', temp)
      if (item?.id) {
        const fetchItem = async () => {
          try {
            const { data, status } = await getLocation(item.id)
            if (status === 200) {
              setValue('stage', data.stage?.id ?? '__none__', {
                shouldValidate: false,
              })
              setValue('type', data.type.id, { shouldValidate: true })
              setValue('latitude', data.latitude, { shouldValidate: true })
              setValue('longitude', data.longitude, { shouldValidate: true })
              setValue('published', data.published, { shouldValidate: true })
              if (!!data.translations && data.translations.length > 0) {
                setTimeout(() => {
                  map(data.translations, (trans) => {
                    const index = findIndex(
                      allLanguages,
                      (l) => l.locale === trans.locale,
                    )
                    if (index !== -1) {
                      setValue(
                        `content.${index}.description`,
                        trans.description,
                      )
                      setValue(`content.${index}.name`, trans.name)
                      setValue(`content.${index}.locale`, trans.locale)
                    }
                  })
                }, 100)
              }
            }
          } catch (err) {
            console.log(err)
          }
        }
        fetchItem()
      }
    }

    return () => {
      reset()
    }
  }, [item, visible])

  const getStages = useCallback(async () => {
    try {
      const { data, status } = await getAllStages({ size: 100000 })
      if (status === 200) setStagesList(data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }, [])

  const getTypes = useCallback(async () => {
    try {
      const { data, status } = await mapTypes({ size: 100000, page: 0 })
      if (status === 200) setMapTypesList(data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }, [])

  useEffect(() => {
    getStages()
  }, [getStages])

  useEffect(() => {
    getTypes()
  }, [getTypes])

  const handleClose = () => {
    reset()
    reset({ content: [] })
    setVisible(false)
  }

  const schema = yup.object().shape({
    type: yup.string().required(),
    content: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          name: yup.string().required('Field is required'),
          description: yup.string().required('Field is required'),
        }),
      )
      .required(),
    stage: yup.string().optional(),
    latitude: yup.string().required('Field is required'),
    longitude: yup.string().required('Field is required'),
    published: yup.boolean().required('Field is required'),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    setValue,
    reset,
  } = useForm<IMapLocationValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const deleteFuncLocation = async () => {
    try {
      if (item) {
        const { status } = await deleteLocations([item.id])
        const resp = await mapLocations({ page, size })

        if (status === 204 && resp.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
    handleClose()
  }

  const submit = async (values: IMapLocationValues) => {
    const data: MapLocationPayload = {
      typeId: values.type,
      latitude: values.latitude,
      longitude: values.longitude,
      published: values.published,
      translations: map(values.content, (one) => ({
        locale: one.locale,
        name: one.name,
        description: one.description,
      })),
    }

    if (values.stage && values.stage !== '__none__') {
      data.stageId = values.stage
    }
    try {
      setLoading(true)
      if (item?.id) {
        const { status } = await updateLocation(item.id, data)
        if (status === 200) {
          handleClose()
          onClose?.()
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } else {
        const { status } = await createLocation(data)
        if (status === 200) {
          handleClose()
          onClose?.()
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.response) {
        setIsError(true)
        setShowMessageBox(true)
        if (e.response.status === 409) {
          setMessage(e.response.data.developerMessage)
        } else {
          setMessage(e.message)
        }
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) {
            handleClose()
          }
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Map"
              subtitle={
                item?.id ? `Edit Location ${item.name}` : 'Add New Location'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {item?.id ? (
              <Button
                onClick={() => setShowDeleteConfirmationModalLocation(true)}
                variant="destructive"
                className="me-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={loading || Object.keys(dirtyFields).length === 0}
                form="location-form"
                type="submit"
                variant="primary"
                loading={loading}
              >
                {item?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="location-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Location Type" error={errors?.type?.message}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <SelectField
                  placeholder="Select Location Type"
                  options={map(mapTypesList, (type) => ({
                    label: type.name,
                    value: type.id,
                  }))}
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  error={errors?.type?.message}
                />
              )}
            />
          </FormGroup>
          <div className="flex w-full flex-col gap-2">
            {map(allLanguages, (language, index) => {
              return (
                <Card
                  className="p-4 flex flex-col space-y-4 bg-monochrome-120 border-none"
                  key={language.id}
                >
                  <FormGroup
                    label="Language"
                    labelClassName="whitespace-nowrap"
                  >
                    <Input
                      value={language.name}
                      disabled
                      className="bg-monochrome-120 border-none"
                    />
                  </FormGroup>

                  <FormGroup
                    label="Name"
                    error={errors?.content?.[index]?.name?.message}
                  >
                    <Input
                      isInvalid={!!errors?.content?.[index]?.name?.message}
                      {...register(`content.${index}.name`)}
                      type="text"
                      placeholder="Enter Name"
                      className="bg-monochrome-5-50"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Description"
                    error={errors?.content?.[index]?.description?.message}
                  >
                    <Textarea
                      isInvalid={
                        !!errors?.content?.[index]?.description?.message
                      }
                      {...register(`content.${index}.description`)}
                      rows={5}
                      placeholder="Enter Description"
                      className="bg-monochrome-5-50"
                    />
                  </FormGroup>
                </Card>
              )
            })}
          </div>
          <FormGroup label="Stage (Optional)" error={errors?.stage?.message}>
            <Controller
              name="stage"
              control={control}
              render={({ field }) => (
                <SelectField
                  placeholder="Select Stage"
                  outline
                  options={[
                    { label: 'No Stage', value: '__none__' },
                    ...map(stagesList, (stage) => ({
                      label: stage.name,
                      value: stage.id,
                    })),
                  ]}
                  value={field.value || '__none__'}
                  onValueChange={field.onChange}
                  error={errors?.stage?.message}
                />
              )}
            />
          </FormGroup>
          <FormGroup label="Latitude" error={errors?.latitude?.message}>
            <Input
              isInvalid={!!errors?.latitude?.message}
              {...register('latitude')}
              type="text"
              placeholder="Enter Latitude"
              className="bg-monochrome-5-50"
            />
          </FormGroup>
          <FormGroup label="Longitude" error={errors?.longitude?.message}>
            <Input
              isInvalid={!!errors?.longitude?.message}
              {...register('longitude')}
              type="text"
              placeholder="Enter Longitude"
              className="bg-monochrome-5-50"
            />
          </FormGroup>
          <FormGroup
            label="Published"
            direction="row"
            labelClassName="whitespace-nowrap"
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  className="border-monochrome-1"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModalLocation}
        setDeleteConfirmationModal={setShowDeleteConfirmationModalLocation}
        deleteTitle={'Location'}
        deleteFunc={deleteFuncLocation}
        deletedItem={item?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
